import React, { useState, useRef } from 'react';
import './EmailClient.css';

const BACKEND_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:3001'
  : 'https://rockpaperrunway.com';

// ─── Preset palettes ──────────────────────────────────────────────────────────
const PRESETS = [
  { name: 'Red',    accent: '#E8192C', dark: '#000000', text: '#333333' },
  { name: 'Blue',   accent: '#1A3AFF', dark: '#0a0f2c', text: '#1a1a2e' },
  { name: 'Green',  accent: '#1A7A4A', dark: '#0a1f12', text: '#1a2e20' },
  { name: 'Purple', accent: '#6C22D6', dark: '#150a2e', text: '#1a1030' },
  { name: 'Orange', accent: '#D95B0A', dark: '#1a0d03', text: '#2e1a00' },
  { name: 'Slate',  accent: '#334155', dark: '#0f172a', text: '#1e293b' },
];

// ─── Compress image to max 800px, quality 0.82 ───────────────────────────────
function compressImage(file, maxWidth = 800, quality = 0.82) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.src = url;
  });
}

// ─── Upload to backend, returns hosted public URL ────────────────────────────
async function uploadMedia(file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${BACKEND_URL}/api/upload-media`, { method: 'POST', body: formData });
  if (!res.ok) throw new Error('Upload failed: ' + res.statusText);
  const data = await res.json();
  return data.url;
}

// ─── Hex colour input (swatch + text field) ───────────────────────────────────
function HexInput({ label, value, onChange }) {
  const [hex, setHex] = useState(value);
  const isValid = (h) => /^#[0-9A-Fa-f]{6}$/.test(h);

  const handleText = (e) => {
    let v = e.target.value;
    if (!v.startsWith('#')) v = '#' + v;
    setHex(v);
    if (isValid(v)) onChange(v);
  };

  const handlePicker = (e) => {
    setHex(e.target.value);
    onChange(e.target.value);
  };

  // sync if parent updates
  React.useEffect(() => { setHex(value); }, [value]);

  return (
    <div className="hex-input-group">
      <label className="hex-label">{label}</label>
      <div className="hex-input-row">
        <input type="color" value={isValid(hex) ? hex : '#000000'} onChange={handlePicker} className="color-swatch-input" />
        <input
          type="text"
          value={hex}
          onChange={handleText}
          maxLength={7}
          placeholder="#000000"
          className={`hex-text-input${!isValid(hex) ? ' invalid' : ''}`}
        />
      </div>
    </div>
  );
}

// ─── Section Block wrapper ────────────────────────────────────────────────────
function SectionBlock({ id, title, emoji, children, onRemove, accentColor }) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <section className="email-section" style={{ border: `1px solid ${accentColor}33` }}>
      <div className="section-block-header">
        <div className="section-block-title">
          <span>{emoji}</span>
          <h3 style={{ margin: 0 }}>{title}</h3>
        </div>
        <div className="section-block-actions">
          <button type="button" className="block-action-btn" onClick={() => setCollapsed(v => !v)}>
            {collapsed ? '▼ Expand' : '▲ Collapse'}
          </button>
          <button type="button" className="block-action-btn remove-btn" onClick={() => onRemove(id)}>
            ✕ Remove
          </button>
        </div>
      </div>
      {!collapsed && <div className="section-block-body">{children}</div>}
    </section>
  );
}

// ─── Media Block ──────────────────────────────────────────────────────────────
function MediaBlock({ block, onUpdate, onRemove, accentColor }) {
  const fileInputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    setUploading(true);

    try {
      if (file.type.startsWith('video/')) {
        // Upload video directly
        const hostedUrl = await uploadMedia(file);
        onUpdate(block.id, { ...block, src: hostedUrl, previewSrc: hostedUrl, fileName: file.name, type: 'video' });
      } else {
        // Compress image first for preview, then upload compressed version
        const compressed = await compressImage(file);
        // Convert compressed base64 back to a Blob for upload
        const res = await fetch(compressed);
        const blob = await res.blob();
        const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });
        const hostedUrl = await uploadMedia(compressedFile);
        onUpdate(block.id, { ...block, src: hostedUrl, previewSrc: compressed, fileName: file.name, type: 'image' });
      }
    } catch (err) {
      setError('Upload failed: ' + err.message);
      // Fallback: store compressed base64 locally (won't work in email but better than nothing)
      if (!file.type.startsWith('video/')) {
        const compressed = await compressImage(file);
        onUpdate(block.id, { ...block, src: compressed, previewSrc: compressed, fileName: file.name, type: 'image', uploadFailed: true });
      }
    }
    setUploading(false);
  };

  return (
    <div className="media-block" style={{ borderColor: `${accentColor}44` }}>
      <div className="media-block-toolbar">
        <span className="media-block-label">{block.type === 'video' ? '🎬 Video' : '🖼️ Image'} Block</span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {uploading && <span style={{ fontSize: '0.78rem', color: '#888' }}>⏳ Uploading…</span>}
          <button type="button" className="block-action-btn" onClick={() => fileInputRef.current.click()} disabled={uploading}>
            {block.src ? '↺ Replace' : '+ Upload'}
          </button>
          <button type="button" className="block-action-btn remove-btn" onClick={() => onRemove(block.id)}>✕</button>
        </div>
      </div>

      <input ref={fileInputRef} type="file" accept="image/*,video/*" style={{ display: 'none' }} onChange={handleFileChange} />

      {error && <p style={{ color: '#ef4444', fontSize: '0.8rem', margin: '6px 0' }}>⚠️ {error}</p>}
      {block.uploadFailed && <p style={{ color: '#f59e0b', fontSize: '0.78rem', margin: '4px 0' }}>⚠️ Stored locally — image may not display in email. Check /api/upload-media is working.</p>}

      {!block.src && !uploading && (
        <div className="media-block-empty" onClick={() => fileInputRef.current.click()}>
          <span>Click to upload image or video</span>
          <small>Images are auto-compressed. Videos upload directly.</small>
        </div>
      )}

      {uploading && (
        <div className="media-block-empty" style={{ cursor: 'default' }}>
          <span>⏳ Uploading & processing…</span>
        </div>
      )}

      {block.src && !uploading && block.type === 'image' && (
        <img src={block.previewSrc || block.src} alt={block.alt || ''} style={{ maxWidth: '100%', borderRadius: 6, display: 'block' }} />
      )}

      {block.src && !uploading && block.type === 'video' && (
        <div style={{ background: '#111', borderRadius: 6, padding: '16px', textAlign: 'center', color: '#fff' }}>
          <div style={{ fontSize: '2rem', marginBottom: 8 }}>🎬</div>
          <div style={{ fontSize: '0.85rem', color: '#ccc' }}>{block.fileName || 'Video uploaded'}</div>
          <div style={{ fontSize: '0.75rem', color: '#666', marginTop: 4 }}>Note: email clients show a placeholder — recipients click to view</div>
          <div style={{ fontSize: '0.72rem', color: '#444', marginTop: 4, wordBreak: 'break-all' }}>{block.src}</div>
        </div>
      )}

      {block.src && (
        <input
          type="text"
          className="media-caption-input"
          placeholder="Caption / alt text (optional)"
          value={block.alt || ''}
          onChange={(e) => onUpdate(block.id, { ...block, alt: e.target.value })}
        />
      )}

      <div style={{ marginTop: 8 }}>
        <label style={{ fontSize: '0.8rem', color: '#666' }}>Alignment</label>
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          {['left', 'center', 'right'].map(a => (
            <button key={a} type="button"
              className="align-btn"
              style={block.align === a ? { background: accentColor, color: '#fff', borderColor: accentColor } : {}}
              onClick={() => onUpdate(block.id, { ...block, align: a })}
            >
              {a === 'left' ? '⬅ Left' : a === 'center' ? '↔ Center' : '➡ Right'}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Insert Media Bar ─────────────────────────────────────────────────────────
function InsertMediaBar({ onInsert, accentColor }) {
  return (
    <div className="insert-media-bar">
      <span style={{ fontSize: '0.8rem', color: '#888' }}>Insert:</span>
      <button type="button" className="insert-btn" style={{ borderColor: accentColor, color: accentColor }} onClick={() => onInsert('image')}>🖼️ Image</button>
      <button type="button" className="insert-btn" style={{ borderColor: accentColor, color: accentColor }} onClick={() => onInsert('video')}>🎬 Video</button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function EmailClient() {
  // ── Custom theme (3 free-form hex colours) ──────────────────────────────────
  const [theme, setTheme] = useState({ accent: '#E8192C', dark: '#000000', text: '#333333' });
  const updateTheme = (key, val) => setTheme(prev => ({ ...prev, [key]: val }));

  const ALL_SECTIONS = ['header', 'pressure', 'phase01', 'phase02', 'phase03', 'timeline', 'whatsIncluded', 'cta', 'sender'];
  const [visibleSections, setVisibleSections] = useState(new Set(ALL_SECTIONS));
  const removeSection = (id) => setVisibleSections(prev => { const s = new Set(prev); s.delete(id); return s; });
  const restoreSection = (id) => setVisibleSections(prev => new Set([...prev, id]));
  const show = (id) => visibleSections.has(id);

  const [mediaBlocks, setMediaBlocks] = useState([]);
  const addMediaBlock = (position, type = 'image') => setMediaBlocks(prev => [...prev, { id: Date.now(), position, type, src: null, previewSrc: null, alt: '', align: 'center' }]);
  const updateMediaBlock = (id, data) => setMediaBlocks(prev => prev.map(b => b.id === id ? data : b));
  const removeMediaBlock = (id) => setMediaBlocks(prev => prev.filter(b => b.id !== id));
  const MediaFor = ({ position }) => (
    <>
      {mediaBlocks.filter(b => b.position === position).map(b => (
        <MediaBlock key={b.id} block={b} onUpdate={updateMediaBlock} onRemove={removeMediaBlock} accentColor={theme.accent} />
      ))}
      <InsertMediaBar onInsert={(type) => addMediaBlock(position, type)} accentColor={theme.accent} />
    </>
  );

  // ── Form state ───────────────────────────────────────────────────────────────
  const [recipients, setRecipients] = useState([{ name: '', email: '', company: '' }]);
  const [emailContent, setEmailContent] = useState({
    subject: 'Your Growth Infrastructure Proposal',
    headlineMain: '', subheadline: '', introPara1: '', introPara2: '',
    senderName: 'The RockPaperRunway Team'
  });
  const [pressurePoints, setPressurePoints] = useState({
    sectionTitle: 'The Real Post-Seed Pressure',
    point1: { title: '', desc: '' }, point2: { title: '', desc: '' }, point3: { title: '', desc: '' }
  });
  const [phases, setPhases] = useState({
    phase01: { title: 'Acquisition Precision', subtitle: 'Engineer cost-efficient reach to qualified retail investors.', services: ['', '', ''] },
    phase02: { title: 'Conversion & Onboarding', subtitle: 'Reduce friction from click to activated investor.', services: ['', '', ''] },
    phase03: { title: 'Revenue Intelligence Layer', subtitle: 'Build visibility into unit economics and scale levers.', services: ['', '', ''] }
  });
  const [timeline, setTimeline] = useState({
    title: '10-Week Deployment Roadmap',
    week1: { label: 'Week 1–3', description: 'Research & CAC\nModeling' },
    week2: { label: 'Week 3–6', description: 'Campaign\nDeployment' },
    week3: { label: 'Week 7–10', description: 'Optimization & Scaling\nFramework' }
  });
  const [whatsIncluded, setWhatsIncluded] = useState({
    title: "What's Included",
    items: [
      { text: 'ICP Modeling' }, { text: 'Performance Campaign Setup' },
      { text: 'Creative Testing Framework' }, { text: 'CRO Architecture' },
      { text: 'Revenue Dashboard' }, { text: 'Weekly Growth Sync' }
    ]
  });
  const [cta, setCta] = useState({
    headline: '', subtext: 'Or reply directly to this email',
    buttonText: 'Schedule 30-Minute Growth Strategy Session',
    buttonLink: 'https://www.rockpaperrunway.com/'
  });
  const [status, setStatus] = useState('');
  const [sending, setSending] = useState(false);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const addRecipient = () => setRecipients([...recipients, { name: '', email: '', company: '' }]);
  const removeRecipient = (i) => { if (recipients.length > 1) setRecipients(recipients.filter((_, idx) => idx !== i)); };
  const updateRecipient = (i, field, val) => { const u = [...recipients]; u[i][field] = val; setRecipients(u); };

  const addServiceToPhase = (pk) => setPhases({ ...phases, [pk]: { ...phases[pk], services: [...phases[pk].services, ''] } });
  const removeServiceFromPhase = (pk, i) => { if (phases[pk].services.length > 1) setPhases({ ...phases, [pk]: { ...phases[pk], services: phases[pk].services.filter((_, idx) => idx !== i) } }); };
  const updateService = (pk, i, val) => { const u = [...phases[pk].services]; u[i] = val; setPhases({ ...phases, [pk]: { ...phases[pk], services: u } }); };
  const updatePhaseField = (pk, field, val) => setPhases({ ...phases, [pk]: { ...phases[pk], [field]: val } });

  const addWhatsIncludedItem = () => setWhatsIncluded({ ...whatsIncluded, items: [...whatsIncluded.items, { text: '' }] });
  const removeWhatsIncludedItem = (i) => { if (whatsIncluded.items.length > 1) setWhatsIncluded({ ...whatsIncluded, items: whatsIncluded.items.filter((_, idx) => idx !== i) }); };
  const updateWhatsIncludedItem = (i, val) => { const u = [...whatsIncluded.items]; u[i] = { text: val }; setWhatsIncluded({ ...whatsIncluded, items: u }); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setStatus('');
    const allServices = [...phases.phase01.services, ...phases.phase02.services, ...phases.phase03.services].filter(Boolean).map(t => ({ title: t, icon: '✓' }));
    // Strip previewSrc (large base64) before sending — only send the hosted src URL
    const cleanMediaBlocks = mediaBlocks.map(({ previewSrc, ...rest }) => rest);
    let successCount = 0, failCount = 0;
    const results = [];
    for (const recipient of recipients) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/send-proposal`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clientName: recipient.name,
            clientEmail: recipient.email,
            companyName: recipient.company,
            customContent: {
              ...emailContent, services: allServices, pressurePoints, phases, timeline,
              whatsIncluded, cta, visibleSections: [...visibleSections],
              mediaBlocks: cleanMediaBlocks, theme
            }
          })
        });
        const data = await response.json();
        if (response.ok) { successCount++; results.push(`✅ ${recipient.name} (${recipient.email})`); }
        else throw new Error(data.message || 'Failed to send');
      } catch (error) {
        failCount++;
        results.push(`❌ ${recipient.name} (${recipient.email}): ${error.message}`);
      }
    }
    setStatus(`<div class="status-results"><strong>Results:</strong><br>${results.join('<br>')}<br><br><strong>Summary: ${successCount} sent, ${failCount} failed</strong></div>`);
    setSending(false);
  };

  const themeStyle = { '--brand-accent': theme.accent, '--brand-dark': theme.dark, '--brand-text': theme.text };
  const removedSections = ALL_SECTIONS.filter(id => !visibleSections.has(id));

  return (
    <div className="email-client" style={themeStyle}>
      <div className="email-header">
        <h2>📧 Growth Proposal Email Builder</h2>
        <p>Fully Customizable Infrastructure Stack Proposal</p>
      </div>

      {/* ── Colour Theme ── */}
      <section className="email-section theme-picker-section">
        <h3 style={{ margin: '0 0 1rem' }}>🎨 Colour Theme</h3>

        <div className="colour-presets">
          <span style={{ fontSize: '0.8rem', color: '#888', marginRight: 8 }}>Quick presets:</span>
          {PRESETS.map(p => (
            <button key={p.name} type="button" title={p.name}
              className="theme-swatch"
              style={{ background: p.accent, outline: theme.accent === p.accent ? `3px solid ${p.accent}` : 'none', outlineOffset: 3 }}
              onClick={() => setTheme({ accent: p.accent, dark: p.dark, text: p.text })}
            >
              {theme.accent === p.accent ? '✓' : ''}
            </button>
          ))}
        </div>

        <div className="hex-inputs-row">
          <HexInput label="🎨 Primary / Accent" value={theme.accent} onChange={v => updateTheme('accent', v)} />
          <HexInput label="🌑 Dark Background" value={theme.dark}   onChange={v => updateTheme('dark', v)} />
          <HexInput label="✏️ Body Text"        value={theme.text}   onChange={v => updateTheme('text', v)} />
        </div>

        <div className="theme-preview-strip">
          <div style={{ background: theme.dark, borderRadius: 6, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ background: theme.accent, width: 32, height: 32, borderRadius: 4 }}></div>
            <span style={{ color: '#fff', fontSize: '0.85rem', fontWeight: 600 }}>Dark section preview</span>
            <div style={{ background: theme.accent, borderRadius: 4, padding: '4px 12px', color: '#fff', fontSize: '0.78rem', fontWeight: 700 }}>BUTTON</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 6, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
            <span style={{ color: theme.accent, fontWeight: 700, fontSize: '0.85rem' }}>✓ Accent on white</span>
            <span style={{ color: theme.text, fontSize: '0.85rem' }}>Body text colour</span>
          </div>
        </div>
      </section>

      {removedSections.length > 0 && (
        <div className="restored-bar">
          <span>Removed:</span>
          {removedSections.map(id => (
            <button key={id} type="button" className="restore-btn" style={{ borderColor: theme.accent, color: theme.accent }} onClick={() => restoreSection(id)}>
              + Restore {id}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit}>

        {/* ── Recipients ── */}
        <section className="email-section">
          <h3>📋 Recipients</h3>
          {recipients.map((recipient, index) => (
            <div key={index} className="recipient-row">
              <input type="text" placeholder="Client Name" value={recipient.name} onChange={(e) => updateRecipient(index, 'name', e.target.value)} required />
              <input type="email" placeholder="email@company.com" value={recipient.email} onChange={(e) => updateRecipient(index, 'email', e.target.value)} required />
              <input type="text" placeholder="Company Name" value={recipient.company} onChange={(e) => updateRecipient(index, 'company', e.target.value)} />
              {recipients.length > 1 && <button type="button" className="btn btn-danger" onClick={() => removeRecipient(index)}>Remove</button>}
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-full" onClick={addRecipient}>+ Add Another Recipient</button>
        </section>
        <MediaFor position="after-recipients" />

        {/* ── Email Header & Intro ── */}
        {show('header') && (
          <SectionBlock id="header" emoji="🎯" title="Email Header & Intro" onRemove={removeSection} accentColor={theme.accent}>
            <div className="email-section-inner dark-section" style={{ background: theme.dark }}>
              <div className="form-group"><label>Email Subject</label><input type="text" value={emailContent.subject} onChange={(e) => setEmailContent({ ...emailContent, subject: e.target.value })} /></div>
              <div className="form-group"><label>Main Headline</label><input type="text" value={emailContent.headlineMain} onChange={(e) => setEmailContent({ ...emailContent, headlineMain: e.target.value })} placeholder="Scaling fractional ownership isn't simple." /></div>
              <div className="form-group"><label>Subheadline</label><textarea rows="2" value={emailContent.subheadline} onChange={(e) => setEmailContent({ ...emailContent, subheadline: e.target.value })} /></div>
              <div className="form-group">
                <label>Intro Paragraph 1</label>
                <textarea rows="2" value={emailContent.introPara1} onChange={(e) => setEmailContent({ ...emailContent, introPara1: e.target.value })} placeholder="We've been reviewing {company}'s positioning..." />
                <small style={{ color: '#aaa', fontSize: '0.82rem' }}>Use {'{company}'} to insert company name</small>
              </div>
              <div className="form-group"><label>Intro Paragraph 2</label><textarea rows="2" value={emailContent.introPara2} onChange={(e) => setEmailContent({ ...emailContent, introPara2: e.target.value })} /></div>
            </div>
            <MediaFor position="after-header" />
          </SectionBlock>
        )}

        {/* ── Pressure Points ── */}
        {show('pressure') && (
          <SectionBlock id="pressure" emoji="⚠️" title="Pressure Section (3 Columns)" onRemove={removeSection} accentColor={theme.accent}>
            <div className="form-group"><label>Section Title</label><input type="text" value={pressurePoints.sectionTitle} onChange={(e) => setPressurePoints({ ...pressurePoints, sectionTitle: e.target.value })} /></div>
            <p className="section-hint">Displayed in the dark section with accent underlines</p>
            <div className="pressure-grid">
              {['point1', 'point2', 'point3'].map((key, index) => (
                <div key={key} className="pressure-column">
                  <label style={{ color: theme.accent }}>Column {index + 1}</label>
                  <input type="text" placeholder="Title" value={pressurePoints[key].title} onChange={(e) => setPressurePoints({ ...pressurePoints, [key]: { ...pressurePoints[key], title: e.target.value } })} />
                  <textarea rows="2" placeholder="Description" value={pressurePoints[key].desc} onChange={(e) => setPressurePoints({ ...pressurePoints, [key]: { ...pressurePoints[key], desc: e.target.value } })} />
                </div>
              ))}
            </div>
            <MediaFor position="after-pressure" />
          </SectionBlock>
        )}

        {/* ── Phases ── */}
        {['phase01', 'phase02', 'phase03'].map((pk, idx) => show(pk) && (
          <SectionBlock key={pk} id={pk} emoji={['📍','🔄','📊'][idx]} title={`Phase 0${idx+1}`} onRemove={removeSection} accentColor={theme.accent}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <span className="phase-badge" style={{ background: theme.accent }}>PHASE 0{idx+1}</span>
            </div>
            <div className="form-group"><label>Phase Title</label><input type="text" value={phases[pk].title} onChange={(e) => updatePhaseField(pk, 'title', e.target.value)} /></div>
            <div className="form-group"><label>Phase Subtitle</label><input type="text" value={phases[pk].subtitle} onChange={(e) => updatePhaseField(pk, 'subtitle', e.target.value)} /></div>
            <p className="section-hint">Services/Deliverables:</p>
            {phases[pk].services.map((service, index) => (
              <div key={index} className="service-row">
                <input type="text" placeholder="Service/Deliverable" value={service} onChange={(e) => updateService(pk, index, e.target.value)} />
                {phases[pk].services.length > 1 && <button type="button" className="btn btn-danger" onClick={() => removeServiceFromPhase(pk, index)}>×</button>}
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-full" onClick={() => addServiceToPhase(pk)}>+ Add to Phase 0{idx+1}</button>
            <MediaFor position={`after-${pk}`} />
          </SectionBlock>
        ))}

        {/* ── Timeline ── */}
        {show('timeline') && (
          <SectionBlock id="timeline" emoji="📅" title="Timeline / Roadmap" onRemove={removeSection} accentColor={theme.accent}>
            <div className="form-group"><label>Section Title</label><input type="text" value={timeline.title} onChange={(e) => setTimeline({ ...timeline, title: e.target.value })} /></div>
            <div className="pressure-grid">
              {['week1', 'week2', 'week3'].map((key, index) => (
                <div key={key} className="pressure-column">
                  <label style={{ color: theme.accent }}>Milestone {index + 1}</label>
                  <input type="text" placeholder="Label (e.g., Week 1–3)" value={timeline[key].label} onChange={(e) => setTimeline({ ...timeline, [key]: { ...timeline[key], label: e.target.value } })} />
                  <textarea rows="2" placeholder="Description" value={timeline[key].description} onChange={(e) => setTimeline({ ...timeline, [key]: { ...timeline[key], description: e.target.value } })} />
                </div>
              ))}
            </div>
            <MediaFor position="after-timeline" />
          </SectionBlock>
        )}

        {/* ── What's Included ── */}
        {show('whatsIncluded') && (
          <SectionBlock id="whatsIncluded" emoji="✅" title="What's Included" onRemove={removeSection} accentColor={theme.accent}>
            <div className="form-group"><label>Section Title</label><input type="text" value={whatsIncluded.title} onChange={(e) => setWhatsIncluded({ ...whatsIncluded, title: e.target.value })} /></div>
            <p className="section-hint">Items (displayed in 2 columns)</p>
            {whatsIncluded.items.map((item, index) => (
              <div key={index} className="service-row">
                <input type="text" placeholder="Item" value={item.text} onChange={(e) => updateWhatsIncludedItem(index, e.target.value)} />
                {whatsIncluded.items.length > 1 && <button type="button" className="btn btn-danger" onClick={() => removeWhatsIncludedItem(index)}>×</button>}
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-full" onClick={addWhatsIncludedItem}>+ Add Item</button>
            <MediaFor position="after-whats-included" />
          </SectionBlock>
        )}

        {/* ── CTA ── */}
        {show('cta') && (
          <SectionBlock id="cta" emoji="🎯" title="Call-to-Action (CTA)" onRemove={removeSection} accentColor={theme.accent}>
            <div className="form-group">
              <label>CTA Headline</label>
              <input type="text" value={cta.headline} onChange={(e) => setCta({ ...cta, headline: e.target.value })} placeholder="If {company} is preparing for aggressive retail scale..." />
              <small style={{ color: '#999', fontSize: '0.82rem' }}>Use {'{company}'} to insert company name</small>
            </div>
            <div className="form-group"><label>Button Text</label><input type="text" value={cta.buttonText} onChange={(e) => setCta({ ...cta, buttonText: e.target.value })} /></div>
            <div className="form-group"><label>Button Link (URL)</label><input type="url" value={cta.buttonLink} onChange={(e) => setCta({ ...cta, buttonLink: e.target.value })} /></div>
            <div className="form-group"><label>Subtext (below button)</label><input type="text" value={cta.subtext} onChange={(e) => setCta({ ...cta, subtext: e.target.value })} /></div>
            <MediaFor position="after-cta" />
          </SectionBlock>
        )}

        {/* ── Sender ── */}
        {show('sender') && (
          <SectionBlock id="sender" emoji="✍️" title="Sender / Footer Signature" onRemove={removeSection} accentColor={theme.accent}>
            <div className="form-group"><label>Sender Name</label><input type="text" value={emailContent.senderName} onChange={(e) => setEmailContent({ ...emailContent, senderName: e.target.value })} /></div>
            <MediaFor position="after-sender" />
          </SectionBlock>
        )}

        <MediaFor position="before-submit" />

        <div className="email-preview-box">
          <small>
            📧 Each email is personalized with recipient name and company. &nbsp;
            Accent: <strong style={{ color: theme.accent }}>{theme.accent}</strong> · {[...visibleSections].length} sections active
          </small>
        </div>

        <button type="submit" className="btn btn-red btn-full submit-btn" style={{ background: theme.accent }} disabled={sending}>
          {sending ? `Sending to ${recipients.length} recipient(s)…` : `🚀 Send Growth Proposals (${recipients.length})`}
        </button>

        {status && <div className="email-status" dangerouslySetInnerHTML={{ __html: status }} />}
      </form>
    </div>
  );
}